import {
  useRemoveRelatedAssetMutation,
  useUploadRelatedAssetMutation,
} from "@/hooks/assets";
import { useToast } from "@/hooks/toast";
import Button from "@ingka/button/Button";
import FormField from "@ingka/form-field/FormField";
import trashCan from "@ingka/ssr-icon/paths/trash-can";
import Text from "@ingka/text";
import { AssetDtoContentTypeEnum, RelatedAssetDtoTypeEnum } from "generated";
import { Fragment, useRef, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import LocaleSelectModal from "./LocaleSelectModal";
import { getAcceptedFileTypes, validateFile } from "./utils";
import { useStore } from "@/services/store";

type RelatedAssetProps = {
  assetId: number;
  languageSelectLabel: string;
  uploadButtonLabel: string;
  validationMessageKey: string;
  fileUploadType: RelatedAssetDtoTypeEnum;
  shouldValidate?: boolean;
  isLockedForOwner: boolean;
};

const RelatedAsset = ({
  assetId,
  languageSelectLabel,
  uploadButtonLabel,
  validationMessageKey,
  fileUploadType,
  shouldValidate,
  isLockedForOwner,
}: RelatedAssetProps) => {
  const { toast } = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [isRelatedAssetModalOpen, setIsRelatedAssetModalOpen] = useState(false);
  const uploadRelatedAsset = useUploadRelatedAssetMutation(assetId);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const removeAssetMutation = useRemoveRelatedAssetMutation(assetId);
  const { asset, resetValidationError } = useStore(
    useShallow((state) => ({
      asset: state.videoAssets[assetId],
      resetValidationError: state.resetVideoValidationError,
    })),
  );

  const relatedAssets = asset.relatedAssets.filter(
    (relatedAsset) => relatedAsset.type === fileUploadType,
  );

  const onModalUpload = async (selectedLocale: string | null) => {
    if (file && selectedLocale) {
      await uploadRelatedAsset.mutateAsync({
        assetId,
        file,
        locale: selectedLocale,
      });
      setFile(null);
      setIsRelatedAssetModalOpen(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      resetValidationError(assetId, "transcript");
      resetValidationError(assetId, "caption");
    }
  };

  const onUploadButtonClick = (fileType: AssetDtoContentTypeEnum) => {
    if (fileInputRef.current) {
      fileInputRef.current.accept = getAcceptedFileTypes(fileType);
      fileInputRef.current.click();
    }
  };

  const onUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];

    if (file.size === 0) {
      toast(
        "The uploaded file is empty. Please upload a valid file before continuing.",
      );
      fileInputRef.current!.value = "";
      return;
    }

    if (!validateFile(file, fileUploadType)) {
      toast("Invalid file type");
      fileInputRef.current!.value = "";
      return;
    }
    setFile(file);
    setIsRelatedAssetModalOpen(true);
  };

  const onRemoveClick = async (relatedAssetId: string) => {
    await removeAssetMutation.mutateAsync({
      assetId,
      relatedAssetId,
    });
  };

  const onModalCancel = () => {
    if (fileInputRef.current) {
      setFile(null);
      fileInputRef.current.value = "";
      setIsRelatedAssetModalOpen(false);
    }
  };

  return (
    <>
      <FormField
        shouldValidate={
          shouldValidate &&
          asset?.validationMessage.some((v) => v.field === validationMessageKey)
        }
        validation={{
          msg: asset?.validationMessage?.find(
            (v) => v.field === validationMessageKey,
          )?.message,
        }}
      >
         <Button
           disabled={isLockedForOwner}
           className="w-full"
           size="small"
           type="secondary"
           onClick={() => onUploadButtonClick(fileUploadType)}
         >
           {uploadButtonLabel}
         </Button>
      </FormField>
      <ul>
        <li>
          <div className="flex max-w-[19rem] flex-col items-center gap-x-space100">
            <div className="grid w-full grid-cols-[2.5rem_1fr_2.5rem]">
              {!!relatedAssets.length && (
                <>
                  <Text tagName="span" className="col-start-2 row-start-1">
                    Filename
                  </Text>
                  <Text tagName="span" className="col-start-3 row-start-1">
                    Locale
                  </Text>
                  <div className="col-span-2 col-start-2 border-b-2 pb-space50" />
                </>
              )}
              {relatedAssets.map((relatedAsset) => {
                return (
                  <Fragment key={relatedAsset?.id}>
                    <Button
                      disabled={isLockedForOwner}
                      aria-label={`Remove ${relatedAsset?.filename}`}
                      className={`col-start-1`}
                      iconOnly
                      size="small"
                      type="tertiary"
                      ssrIcon={trashCan}
                      onClick={async () => {
                        await onRemoveClick(relatedAsset?.id);
                      }}
                    />
                    <Text
                      tagName="span"
                      className={`col-start-2 block self-center truncate`}
                      title={relatedAsset?.filename}
                    >
                      {relatedAsset?.filename}
                    </Text>
                    <Text
                      tagName="span"
                      className={`col-start-3 block self-center`}
                    >
                      {relatedAsset.locale}
                    </Text>
                  </Fragment>
                );
              })}
            </div>
          </div>
        </li>
      </ul>
      <input
        ref={fileInputRef}
        className="hidden"
        type="file"
        onChange={onUpload}
      />
      <LocaleSelectModal
        uploadButtonLabel={uploadButtonLabel}
        languageSelectLabel={languageSelectLabel}
        isModalOpen={isRelatedAssetModalOpen}
        file={file}
        isUploading={uploadRelatedAsset.status === "pending"}
        onModalClose={onModalCancel}
        onModalUpload={onModalUpload}
      />
    </>
  );
};

export default RelatedAsset;
